import {useEffect, useMemo, useState} from 'react';
import {MenuButton} from '../components/menu-button.js';
import {
    Container,
    Image,
    Panel9Slice,
    Row,
    Column,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, Justify} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Material} from '@wonderlandengine/api';
import {parseColor} from '../utils/utils.js';
import {GameFlowController} from '../../components/game-flow-controller.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {goToMenu} from '../utils/goToMenu.js';
import {MenuPanels} from '../../models/UIModel.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {GameOverModel} from '../../models/GameOverModel.js';
import {WindowHeader} from '../components/window-header.js';
import {AdManager, AdRewardTypes} from '../../components/ad-manager.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {ConfirmationDialogController} from './confirmation-dialog.js';
import {Localization} from '../../utils/Localization.js';
import {MalRoot} from '../components/mal-root.js';
import {NotificationLevel} from '../../enums/NotificationLevel.js';
import {user} from '@wonderlandengine/uber-sdk';
import {RNG} from '../../utils/rng.js';

const shareObject = {
    title: 'Rise of the Blobs',
    text: `I just scored 12345 in Rise of the Blobs!`,
    url: 'https://riseoftheblobs.com',
};
function share(score: string) {
    navigator
        .share({
            title: 'Rise of the Blobs',
            text: `I just scored ${score} in Rise of the Blobs!`,
            url: 'https://riseoftheblobs.com',
        })
        .then(() => {})
        .catch((e) => {
            console.log(e);
        });
}

function adForCoins({e}, coins: number) {
    AdManager.instance
        .getAdReward(AdRewardTypes.DoubleCoins, e, coins)
        .then(({type, coins}) => {
            if (type === AdRewardTypes.DoubleCoins) {
                ConfirmationDialogController.instance.displayDialog(
                    `You got ${formatNumber(coins)} coins!`,
                    'YEAH!'
                );
                //TK: Because we need to hide the UI and thus cause a redraw, we need to store the
                // doubled coins in the model, so that the UI can update.
                GameOverModel.instance.setCoins(GameOverModel.instance.coins + coins);
            }
        });
}

function fontSizeFromScore(score: string) {
    const len = score.length;
    if (len < 6) return 64;
    if (len < 10) return 48;
    if (len < 14) return 38;
    return 28;
}

interface ResultsRootProps {
    highScoreText: string;
    score: number;
    coins: number;
    totalCoins: number;
}

const ResultsRoot = ({highScoreText, score, coins, totalCoins}: ResultsRootProps) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const scoreBackground = useMemo(() => rotbMats.whiteBevelMaterial!.clone(), []);
    (scoreBackground as FlatOpaqueTextured).setColor(
        parseColor(colorSwatch.ScoreBackground)
    );
    const scoreBorder = useMemo(() => rotbMats.whiteBevelMaterial!.clone(), []);
    (scoreBorder as FlatOpaqueTextured).setColor(parseColor(colorSwatch.ScoreBorder));
    const [adForCoinsButtonVisible, setAdForCoinsButtonVisible] = useState(
        GameOverModel.instance.adForCoinsButtonVisible
    );

    const [formattedScore, setFormattedScore] = useState(formatNumber(score));
    const [isLoggedIn, setIsLoggedIn] = useState(user.isLoggedIn);

    const showShare = useMemo<boolean>(() => {
        return (
            navigator.share !== undefined &&
            navigator.canShare !== undefined &&
            navigator.canShare(shareObject)
        );
    }, []);

    useEffect(() => {
        const propertyChanged = (prop) => {
            if (prop === 'adForCoinsButtonVisible') {
                setAdForCoinsButtonVisible(GameOverModel.instance.adForCoinsButtonVisible);
            }
        };
        GameOverModel.instance.propertyChanged.add(propertyChanged);

        return () => {
            GameOverModel.instance.propertyChanged.remove(propertyChanged);
        };
    }, []);

    useEffect(() => {
        setFormattedScore(formatNumber(score));
    }, [score]);

    return (
        <Container margin={20}>
            <Container
                justifyContent={Justify.FlexEnd}
                alignContent={Align.Center}
                alignItems={Align.FlexEnd}
            >
                <Text
                    z={0.01}
                    textAlign="right"
                    top={-6}
                    fontSize={20}
                    text={highScoreText}
                ></Text>
            </Container>
            {!isLoggedIn && (
                <Container marginBottom={10}>
                    <Text
                        fontSize={12}
                        textAlign="center"
                        text={Localization.instance.get('TutorialMenus3')}
                    ></Text>
                </Container>
            )}
            <Panel9Slice
                width="100%"
                height={120}
                borderSize={6}
                borderTextureSize={0.08}
                material={scoreBackground}
                padding={16}
            >
                <Row
                    width="100%"
                    height="100%"
                    justifyContent={Justify.SpaceBetween}
                    alignItems={Align.Center}
                >
                    <Container>
                        {showShare && (
                            <MenuButton
                                height={60}
                                color={colorSwatch.ShareButton}
                                fontSize={20}
                                width={125}
                                onClick={() => share(formattedScore)}
                            >
                                {Localization.instance.get('Share')}
                            </MenuButton>
                        )}
                    </Container>
                    <Container paddingTop={fontSizeFromScore(formattedScore) / 2}>
                        <Text
                            textAlign="right"
                            fontSize={fontSizeFromScore(formattedScore)}
                            color={colorSwatch.DescriptionText}
                        >
                            {formattedScore}
                        </Text>
                    </Container>
                </Row>
            </Panel9Slice>
            <Panel9Slice
                width="100%"
                height={90}
                borderSize={6}
                borderTextureSize={0.08}
                material={scoreBorder}
                paddingLeft={16}
            >
                <Row
                    width="100%"
                    height="100%"
                    justifyContent={Justify.SpaceBetween}
                    alignItems={Align.Center}
                >
                    <Container>
                        {/* <ColoredImage
                            rounding={0}
                            height={50}
                            src={UiTexturesManager.instance.info}
                            color={colorSwatch.ScoreInfoIcon}
                            width={50}
                            marginLeft={40}
                        ></ColoredImage> */}
                        {adForCoinsButtonVisible && (
                            <MenuButton
                                top={-4}
                                height={60}
                                color={colorSwatch.GetCoinsButton}
                                fontSize={22}
                                width={280}
                                iconWidth={50}
                                iconHeight={50}
                                icon={UiTexturesManager.instance.coinDuplicator}
                                animate={true}
                                onClick={({e}) => {
                                    GameOverModel.instance.setAdForCoinsButtonVisible(
                                        false
                                    );
                                    adForCoins({e}, coins);
                                }}
                                text={` ${Localization.instance.get('DoubleCoins')} `}
                            ></MenuButton>
                        )}
                    </Container>
                    <Container
                        justifyContent={Justify.Center}
                        alignItems={Align.FlexEnd}
                        marginRight={18}
                    >
                        <Text
                            textAlign="right"
                            marginBottom={4}
                            fontSize={18}
                            color={colorSwatch.ScoreCoinText}
                        >
                            {`+ ${formatNumber(coins)}`}
                        </Text>
                        <Row>
                            <Image
                                rounding={0}
                                width={20}
                                height={20}
                                marginRight={4}
                                src={UiTexturesManager.instance.coinIconSmall}
                            ></Image>

                            <Container>
                                <Text
                                    textAlign="right"
                                    fontSize={24}
                                    color={colorSwatch.ScoreCoinText}
                                >
                                    {formatNumber(totalCoins)}
                                </Text>
                            </Container>
                        </Row>
                    </Container>
                </Row>
            </Panel9Slice>
            <Container alignSelf={Align.Center} width={300}>
                <MenuButton
                    height={54}
                    color={colorSwatch.MainMenuShoppe}
                    fontSize={18}
                    icon={UiTexturesManager.instance.iconLeaderboard}
                    iconWidth={40}
                    iconHeight={40}
                    margin={10}
                    onClick={(e) => goToMenu(MenuPanels.Leaderboard)}
                >
                    {Localization.instance.get('Leaderboard')}
                </MenuButton>
                <MenuButton
                    height={54}
                    margin={10}
                    color={colorSwatch.MainMenuMissions}
                    fontSize={24}
                    icon={UiTexturesManager.instance.checklist}
                    iconWidth={36}
                    iconHeight={34}
                    onClick={(e) => goToMenu(MenuPanels.MissionRequirements)}
                >
                    {Localization.instance.get('Missions')}
                </MenuButton>
            </Container>
        </Container>
    );
};

let bm: Material | null;
export const GameOverPanel = () => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const material = rotbMats.titleBackgroundMaterial;

    const [gameMode] = useState(GameModeType[GameFlowController.instance.gameMode]);

    if (!bm) {
        bm = material!.clone();
        (bm as any).setColor(parseColor(colorSwatch.TitleBackground));
    }

    const [score, setScore] = useState(GameOverModel.instance.score);
    const [highScoreText, setHighScoreText] = useState(
        GameOverModel.instance.highscoreText
    );
    const [rank, setRank] = useState(GameOverModel.instance.rank);
    const [xpPercentage, setXpPercentage] = useState(GameOverModel.instance.xpPercentage);

    const [coins, setCoins] = useState(GameOverModel.instance.coins);
    const [totalCoins, setTotalCoins] = useState(GameOverModel.instance.totalCoins);

    useEffect(() => {
        const propertyChanged = (prop: string) => {
            switch (prop) {
                case 'score':
                    setScore(GameOverModel.instance.score);
                    break;
                case 'coins':
                    setCoins(GameOverModel.instance.coins);
                    break;
                case 'totalCoins':
                    setTotalCoins(GameOverModel.instance.totalCoins);
                    break;
                case 'highscoreText':
                    setHighScoreText(GameOverModel.instance.highscoreText);
                    break;
                case 'rank':
                    setRank(GameOverModel.instance.rank);
                    break;
                case 'xpPercentage':
                    setXpPercentage(GameOverModel.instance.xpPercentage);
                    break;
            }
        };
        GameOverModel.instance.propertyChanged.add(propertyChanged);
        GameFlowController.instance.notificationsAllowed = NotificationLevel.Everything;
        return () => {
            GameOverModel.instance.propertyChanged.remove(propertyChanged);
        };
    }, []);
    const [gameOverText] = useState(
        Localization.instance.get(
            score === 1
                ? 'GameOverZeroText'
                : RNG.getItem([
                      'GameOverMenuText1',
                      'GameOverMenuText2',
                      'GameOverMenuText3',
                      'GameOverMenuText4',
                      'GameOverMenuText5',
                      'GameOverMenuText6',
                      'GameOverMenuText7',
                      'GameOverMenuText8',
                      'GameOverMenuText9',
                      'GameOverMenuText10',
                      'GameOverMenuText11',
                      'GameOverMenuText12',
                  ])
        )
    );

    return (
        <Column width="100%" height="100%">
            <Container margin={20}>
                <WindowHeader title={Localization.instance.get(gameMode)} />
            </Container>
            {/* <Container margin={10} top={10}>
                <Panel9Slice
                    height={60}
                    width={500}
                    material={bm}
                    borderSize={8}
                    borderTextureSize={0.0625}
                    flexDirection={FlexDirection.Row}
                    alignSelf={Align.Center}
                    border={2}
                    borderColor="#000000"
                >
                    <Text
                        flexGrow={1}
                        fontSize={36}
                        top={-8}
                        textAlign="center"
                        color={colorSwatch.TitleText}
                        text={gameMode}
                    ></Text>
                </Panel9Slice>
            </Container> */}
            <MalRoot
                rank={rank}
                xpPercentage={xpPercentage}
                additionalRotation={[-1.5, 1.5, 0]}
                text={gameOverText}
            ></MalRoot>
            <Container margin={10}></Container>
            <ResultsRoot
                highScoreText={highScoreText}
                score={score}
                coins={coins}
                totalCoins={totalCoins}
            ></ResultsRoot>
        </Column>
    );
};
